import React, { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Lightbulb,
  AlertCircle,
  Sparkles,
  Flag,
  Languages
} from "lucide-react";
import type { Question } from "@/types/quiz";
import FlagQuestionButton from "@/components/quiz/FlagQuestionButton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { useQuizSessionHistory } from "@/hooks/useQuizSessionHistory";
import { AIHybridService } from "@/utils/aiHybridService";
import QuizQuestionContent from "./QuizQuestionContent";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuestionCardProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  selectedAnswer: number | null;
  onSelect: (selected: number) => void;
  onNext: () => void;
  selectedLanguage: 'english' | 'hindi' | 'hinglish';
  showBilingual?: boolean;
  onShowHint?: () => void;
  isLoadingHint?: boolean;
  currentHint?: string | null;
  isTransitioning?: boolean;
  isInputLocked?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  totalQuestions,
  selectedAnswer,
  onSelect,
  onNext,
  selectedLanguage,
  showBilingual = false,
  onShowHint,
  isLoadingHint,
  currentHint,
  isTransitioning = false,
  isInputLocked = false
}) => {
  const [optionHover, setOptionHover] = useState<number | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<{
    question: string;
    options: string[];
  } | null>(null);
  const [showTranslation, setShowTranslation] = useState(false);
  
  const isAnswered = selectedAnswer !== null;
  const { addOrUpdateSnapshot } = useQuizSessionHistory();
  
  useEffect(() => {
    setShowTranslation(false);
    setTranslatedContent(null);
    console.log("Translation state reset for new question:", question.id);
  }, [question.id, questionNumber]);
  
  React.useEffect(() => {
    addOrUpdateSnapshot(question, questionNumber - 1, selectedAnswer);
  }, [question, questionNumber, selectedAnswer, addOrUpdateSnapshot]);
  
  const handleSelect = useCallback((index: number) => {
    if (isAnswered || isTransitioning || isInputLocked) return;
    onSelect(index);
  }, [isAnswered, isTransitioning, isInputLocked, onSelect]);
  
  const isCorrect = isAnswered && selectedAnswer === question.correctanswer;
  
  const difficultyColors = {
    easy: {
      bg: "bg-green-100",
      text: "text-green-700",
      border: "border-green-200",
      badge: "bg-green-50 border-green-200 text-green-700"
    },
    medium: {
      bg: "bg-amber-100",
      text: "text-amber-700",
      border: "border-amber-200",
      badge: "bg-amber-50 border-amber-200 text-amber-700"
    },
    hard: {
      bg: "bg-rose-100",
      text: "text-rose-700",
      border: "border-rose-200",
      badge: "bg-rose-50 border-rose-200 text-rose-700"
    }
  };
  
  const handleTranslate = async () => {
    if (isTranslating || !question || !question.options) return;
    
    if (translatedContent) {
      setShowTranslation(!showTranslation);
      return;
    }
    
    let targetLanguage = "Hindi";
    if (selectedLanguage === 'hinglish') targetLanguage = "Hinglish (mix of Hindi and English)";
    if (selectedLanguage === 'english') targetLanguage = "English";
    
    try {
      setIsTranslating(true);
      toast.info(`Translating to ${selectedLanguage}...`);
      
      const result = await AIHybridService.translateQuizContent(
        question.question,
        question.options,
        targetLanguage
      );
      
      setTranslatedContent({
        question: result.translatedQuestion,
        options: result.translatedOptions
      });
      setShowTranslation(true);
      toast.success(`Translated to ${selectedLanguage}`);
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Failed to translate content");
    } finally {
      setIsTranslating(false);
    }
  };
  
  const questionDifficulty = question.difficulty || "medium";
  const colors = difficultyColors[questionDifficulty as keyof typeof difficultyColors];
  
  const handleOptionHover = useCallback((index: number | null) => {
    if (!isTransitioning) {
      setOptionHover(index);
    }
  }, [isTransitioning]);

  React.useEffect(() => {
    console.log("QuestionCard states:", { 
      isAnswered, 
      isTransitioning,
      isInputLocked,
      selectedAnswer
    });
  }, [isAnswered, isTransitioning, isInputLocked, selectedAnswer]);

  const isMobile = useIsMobile();
  
  // Ensure question and options exist and are valid before rendering
  if (!question || !question.options || !Array.isArray(question.options)) {
    console.error("Invalid question or options:", question);
    return (
      <div className="w-full max-w-3xl mx-auto">
        <Card className="p-8 text-center bg-white shadow-lg rounded-xl">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
          <p className="text-gray-700">Question data is incomplete. Please try again.</p>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-3xl mx-auto animate-fadeIn">
      <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-full px-3 py-1.5 bg-blue-50 text-blue-700 border-blue-200 text-xs">
            Question {questionNumber} of {totalQuestions}
          </Badge>
          
          <Badge variant="outline" className={`rounded-full px-3 py-1.5 capitalize ${colors.badge} text-xs`}>
            {questionDifficulty}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          {question.time_limit && (
            <Badge variant="outline" className="flex items-center gap-1 bg-slate-50 text-slate-700 border-slate-200">
              <Clock className="w-3 h-3" />
              <span>{question.time_limit}s</span>
            </Badge>
          )}
          
          {question.category && (
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
              {question.category} {question.subject && `• ${question.subject}`}
            </Badge>
          )}
        </div>
      </div>
      
      <Card className={`bg-white shadow-md border-t-4 border-t-primary rounded-xl overflow-hidden transition-all duration-300 ${
        isTransitioning || isInputLocked ? 'opacity-70' : ''
      }`}>
        <CardHeader className="bg-gradient-to-r from-slate-50 to-white p-5 border-b">
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-medium text-gray-800 leading-snug">
                {question.question}
              </h3>
              
              {question.ai_generated && (
                <Badge variant="outline" className="flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 ml-2">
                  <Sparkles className="w-3 h-3" />
                  <span>AI</span>
                </Badge>
              )}
            </div>
            
            {showTranslation && translatedContent && (
              <div className="text-sm text-indigo-700 pt-2 border-t border-gray-100">
                {translatedContent.question}
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-5">
          <ScrollArea className={`${isMobile ? 'max-h-[50vh] touch-auto overflow-auto' : 'max-h-[500px]'}`}>
            <div className="space-y-3 pb-2">
              {question.options.map((option, optionIndex) => (
                <button
                  key={optionIndex}
                  onClick={() => handleSelect(optionIndex)}
                  onMouseEnter={() => handleOptionHover(optionIndex)}
                  onMouseLeave={() => handleOptionHover(null)}
                  disabled={isAnswered || isTransitioning || isInputLocked}
                  className={`w-full p-4 rounded-lg text-left flex items-start gap-3 transition-all ${
                    selectedAnswer === optionIndex
                      ? isCorrect
                        ? "bg-green-100 border border-green-300 text-green-800"
                        : "bg-red-100 border border-red-300 text-red-800"
                      : isAnswered && question.correctanswer === optionIndex
                        ? "bg-green-100 border border-green-300 text-green-800"
                        : optionHover === optionIndex && !isAnswered && !isTransitioning && !isInputLocked
                          ? "bg-blue-50 border border-blue-200 text-blue-700"
                          : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700"
                  } ${isAnswered || isTransitioning || isInputLocked ? "cursor-default" : "cursor-pointer"} ${
                    isInputLocked ? "opacity-60" : ""
                  }`}
                >
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                    selectedAnswer === optionIndex
                      ? isCorrect
                        ? "bg-green-200 text-green-800"
                        : "bg-red-200 text-red-800"
                      : isAnswered && question.correctanswer === optionIndex
                        ? "bg-green-200 text-green-800"
                        : "bg-gray-200 text-gray-700"
                  }`}>
                    {isAnswered ? (
                      optionIndex === question.correctanswer ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : selectedAnswer === optionIndex ? (
                        <XCircle className="w-4 h-4" />
                      ) : (
                        String.fromCharCode(65 + optionIndex)
                      )
                    ) : (
                      String.fromCharCode(65 + optionIndex)
                    )}
                  </div>
                  <div className="flex-1 text-gray-800">
                    {option}
                    {showTranslation && translatedContent && translatedContent.options && Array.isArray(translatedContent.options) && 
                     translatedContent.options[optionIndex] && (
                      <div className="mt-1 text-indigo-700 text-sm">
                        {translatedContent.options[optionIndex]}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              
              {isAnswered && question.explanation && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Explanation
                  </h4>
                  <p className="text-blue-700 text-sm">{question.explanation}</p>
                </div>
              )}
              
              {currentHint && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4" />
                    Hint
                  </h4>
                  <p className="text-amber-700 text-sm">{currentHint}</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
        
        <CardFooter className="flex justify-between items-center p-5 bg-gray-50 border-t">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleTranslate}
              disabled={isTransitioning || isTranslating || selectedLanguage === 'english' || isInputLocked}
              className={`flex items-center gap-2 ${
                showTranslation 
                  ? "bg-indigo-100 border-indigo-200 text-indigo-700 hover:bg-indigo-200" 
                  : "bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
              } ${isInputLocked ? "opacity-50 pointer-events-none" : ""}`}
              size="sm"
            >
              <Languages className="w-4 h-4" />
              {isTranslating 
                ? "Translating..." 
                : showTranslation 
                  ? "Hide Translation" 
                  : translatedContent 
                    ? "Show Translation" 
                    : `Translate to ${selectedLanguage}`}
            </Button>
            
            {onShowHint && (
              <Button
                variant="outline"
                onClick={onShowHint}
                disabled={isTransitioning || isLoadingHint || !!currentHint || isInputLocked}
                className="flex items-center gap-2 bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
                size="sm"
              >
                <Lightbulb className="w-4 h-4" />
                {isLoadingHint ? "Loading..." : currentHint ? "Hint Shown" : "Show Hint"}
              </Button>
            )}
            
            <FlagQuestionButton questionId={question.id || ''} size="sm" disabled={isTransitioning || isInputLocked} />
          </div>
          
          {isAnswered && (
            <Button 
              onClick={onNext} 
              className={`flex items-center gap-2 bg-primary hover:bg-primary/90 transition-all ${isInputLocked ? 'opacity-70' : ''}`}
              disabled={isTransitioning || isInputLocked}
              aria-disabled={isTransitioning || isInputLocked}
            >
              {questionNumber === totalQuestions ? "See Results" : "Next Question"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </CardFooter>
      </Card>
      
      <div className="mt-6">
        <Progress value={(questionNumber / totalQuestions) * 100} className="h-2" />
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Progress</span>
          <span>{Math.round((questionNumber / totalQuestions) * 100)}%</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(QuestionCard);
